import { Avatar, Box, IconButton, MenuItem, styled } from "@mui/material";
import type { MouseEvent } from "react";
import useLogout from "@lib/user/useLogout";
import { useState } from "react";
import { useRouter } from "next/navigation";
import ContextMenu from "@components/shared/ContextMenu";
import config from "@lib/config";
import { useAuth } from "@/hooks/useAuth";
import { createPortalSession } from "@lib/api/billing";
import { useSettingsNavigation } from "@lib/settings/SettingsNavigationContext";
import {
  CreditCard,
  LogOut,
  Plug,
  Settings as SettingsIcon,
  ShieldCheck,
} from "lucide-react";

const ProfileContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  columnGap: "1rem",
});

const StyledMenuItem = styled(MenuItem)({
  "&&": {
    justifyContent: "flex-start",
    gap: "var(--space-3)",
  },
  "& svg": {
    flexShrink: 0,
  },
});

const UserDetails = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "var(--space-3)",
  borderBottom: "1px solid #131313",
  padding: "18px",
});

const UserCopy = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "var(--space-1)",
  span: {
    fontSize: "0.8rem",
  },
});

interface UserProfileProps {
  recruiter?: {
    photo_url?: string | null;
    logo_url?: string | null;
    user: { name: string; email: string };
  };
  isAdmin: boolean;
}

export default function UserProfile({ recruiter, isAdmin }: UserProfileProps) {
  const logout = useLogout();
  const router = useRouter();
  const { user } = useAuth();
  const hasBillingAccount = (user as typeof user & { hasBillingAccount?: boolean })
    ?.hasBillingAccount;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { openSettings } = useSettingsNavigation();
  const isMenuOpen = Boolean(anchorEl);

  const avatar = recruiter?.photo_url ?? recruiter?.logo_url;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAdminClick = () => {
    window.location.assign(`${config.apiBaseUrl}/admin`);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsOpen = () => {
    handleClose();
    openSettings();
  };

  const handleManageBilling = async () => {
    handleClose();
    try {
      const url = await createPortalSession();
      window.location.assign(url);
    } catch (err) {
      console.error("Failed to open billing portal:", err);
    }
  };

  return (
    <ProfileContainer>
      <IconButton
        id="user-menu-button"
        aria-controls={isMenuOpen ? "user-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? "true" : undefined}
        onClick={handleClick}
      >
        <Avatar src={avatar} alt={recruiter?.user.name} />
      </IconButton>
      <ContextMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        labelledBy={"user-menu-button"}
      >
        <UserDetails>
          <Avatar src={avatar} alt="" aria-hidden="true" />
          <UserCopy>
            <strong>{recruiter?.user.name}</strong>
            <span>{recruiter?.user.email}</span>
          </UserCopy>
        </UserDetails>
        {isAdmin && (
          <StyledMenuItem onClick={handleAdminClick}>
            <ShieldCheck size={18} aria-hidden="true" />
            Admin dashboard
          </StyledMenuItem>
        )}
        <StyledMenuItem onClick={handleSettingsOpen}>
          <SettingsIcon size={18} aria-hidden="true" />
          Settings
        </StyledMenuItem>
        {hasBillingAccount && (
          <StyledMenuItem onClick={handleManageBilling}>
            <CreditCard size={18} aria-hidden="true" />
            Manage Billing
          </StyledMenuItem>
        )}
        <StyledMenuItem
          onClick={() => {
            handleClose();
            router.push("/integrations");
          }}
        >
          <Plug size={18} aria-hidden="true" />
          Integrations
        </StyledMenuItem>
        <StyledMenuItem onClick={logout}>
          <LogOut size={18} aria-hidden="true" />
          Logout
        </StyledMenuItem>
      </ContextMenu>
    </ProfileContainer>
  );
}
